import React, { useEffect } from 'react';
import { Form, Input, Select, Radio, DatePicker, Row, Col, Checkbox } from 'antd';
import ConditionalFormItem from '../../ConditionalFormItem';
import { SERVICE_TYPE_MAP } from '../../../config/service-type-map';
import NATION_OPTIONS from "../../../config/nations";
import feeService from '../../../services/fee';

const { Option } = Select;

interface PatentBusinessTabProps {
  // 表单实例（保持与 TrademarkBusinessTab 一致的 props 形状，方便调用方传参）
  form: any;
  // 由上层传递 serviceType，本组件内部从 SERVICE_TYPE_MAP 计算选项
  serviceType: string;
  // 只读样式（与其它业务 Tab 保持一致的接口）
  getReadOnlyProps?: (fieldType?: 'input' | 'select') => any;
  // 是否基础国家模式（影响国家字段文案）
  baseCountryMode?: boolean;
}

// 专利业务信息 Tab：从 DynamicOrderForm 中的内联 PatentBasic 提取
const PatentSection: React.FC<PatentBusinessTabProps> = ({ form, serviceType, getReadOnlyProps = () => ({}), baseCountryMode = false }) => {
  const serviceTypeOptions = SERVICE_TYPE_MAP[serviceType] || {};
  const watchedNation = Form.useWatch('nation', form);

  useEffect(() => {
    const loadChannels = async () => {
      try {
        if (!watchedNation) {
          return;
        }
        await feeService.getSubmitChannelsByNation(watchedNation as string);
      } catch (e) {
        // ignore
      }
    };
    loadChannels();
  }, [watchedNation]);

  return (
    <>

      {/* 子业务名称 */}
      <Form.Item label="子业务名称" name="sub_service" rules={[{ required: true, message: '请选择子业务名称' }]}>
        <Select placeholder="请选择具体的子业务名称">
          {(Object.entries(serviceTypeOptions) as [string, string][]).map(([key, value]) => (
            <Option key={key} value={key}>
              {value}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {/* 基本信息（复制自 BasicInfoTab，去除申请人相关） */}
      <Form.Item
        label={baseCountryMode ? '基础国家/地区' : '国家/地区'}
        name="nation"
        rules={[{ required: true, message: baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区' }]}
      >
        <Select
          placeholder={baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区'}
          showSearch
          filterOption={(input, option) =>
            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
          }
          data-testid="order-nation"
        >
          {NATION_OPTIONS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="订单编号" name="order_no" rules={[{ required: true, message: '订单编号自动生成' }]}>
            <Input placeholder="自动生成" readOnly style={{ backgroundColor: '#f5f5f5', color: '#999' }} data-testid="order-no" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="客户案件编号" name="case_no">
            <Input placeholder="请输入客户案件编号" data-testid="order-case-no" />
          </Form.Item>
        </Col>
      </Row>

      <ConditionalFormItem condition="sub_service == authorization || sub_service == rejection_review">
        <Form.Item label="专利类型" name="patent_type" rules={[{ required: true, message: '请选择专利类型' }]}>
          <Radio.Group>
            <Radio value="外观专利">外观专利</Radio>
            <Radio value="实用新型">实用新型</Radio>
            <Radio value="发明专利">发明专利</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      <Form.Item label="专利名" name="patent_name" rules={[{ required: true, message: '请输入专利名' }]}>
        <Input placeholder="请输入专利名" />
      </Form.Item>

      <ConditionalFormItem condition="sub_service == invention_application || sub_service == utility_application">
        <Form.Item label="权利要求数量" required>
          <Form.Item name="claims_count" noStyle initialValue={'未超项'} rules={[{ required: true, message: '请选择权利要求数量' }]}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const checked = form.getFieldValue('claims_count') === '超项';
              return (
                <Checkbox
                  checked={checked}
                  onChange={(e) => form.setFieldsValue({ claims_count: e.target.checked ? '超项' : '未超项' })}
                >
                  超项
                </Checkbox>
              );
            }}
          </Form.Item>
        </Form.Item>
        <ConditionalFormItem condition="claims_count == 超项">
          <Form.Item label="超出数量" name="exceeding_quantity">
            <Input placeholder="请输入超出数量" />
          </Form.Item>
        </ConditionalFormItem>
        <Form.Item label="文件撰写" name="include_document_writing" valuePropName="checked">
          <Checkbox>需要</Checkbox>
        </Form.Item>
      </ConditionalFormItem>

      {/* 专利基础字段 */}
      {/*<ConditionalFormItem condition="sub_service == authorization || sub_service == rejection_review">*/}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="申请号" name="application_no">
              <Input placeholder="请输入申请号" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="注册号" name="registration_no">
              <Input placeholder="请输入注册号" data-testid="order-registration-no" />
            </Form.Item>
          </Col>
        </Row>
      {/*</ConditionalFormItem>*/}

      <ConditionalFormItem condition="sub_service == appearance_application">
        <Form.Item label="是否制图" name="include_drawing" valuePropName="checked">
          <Checkbox>需要制图</Checkbox>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service == appearance_application || sub_service == invention_application || sub_service == utility_application">
        <Form.Item label="是否加速" name="accelerated" valuePropName="checked" initialValue={false}>
          <Checkbox>含加速</Checkbox>
        </Form.Item>
        <Form.Item label="是否含授权" name="include_authorization" valuePropName="checked" initialValue={false}>
          <Checkbox>含授权</Checkbox>
        </Form.Item>
        <Form.Item label="专利翻译" name="include_translation" valuePropName="checked" initialValue={false}>
          <Checkbox>包含</Checkbox>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service != rejection_review">
        <Form.Item label="实体类型" name="entity_type" rules={[{ required: true, message: '请选择实体类型' }]} initialValue={'微实体'}>
          <Radio.Group>
            <Radio value="微实体">微实体</Radio>
            <Radio value="小实体">小实体</Radio>
            <Radio value="大实体">大实体</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service == appearance_application || sub_service == invention_application || sub_service == utility_application">
        <Form.Item label="优先权">
          <Form.Item name="priority" noStyle initialValue={'无优先权'} rules={[{ required: true, message: '请选择是否有优先权' }]}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const checked = form.getFieldValue('priority') === '有优先权';
              return (
                <Checkbox
                  checked={checked}
                  onChange={(e) => form.setFieldsValue({ priority: e.target.checked ? '有优先权' : '无优先权' })}
                >
                  有优先权
                </Checkbox>
              );
            }}
          </Form.Item>
        </Form.Item>
        <ConditionalFormItem condition="priority == 有优先权">
          <Form.Item label="DAS码/申请号" name="priority_no">
            <Input placeholder="请输入DAS码或申请号" />
          </Form.Item>
        </ConditionalFormItem>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service != authorization && sub_service != rejection_review">
        <Form.Item label="公开状态">
          <Form.Item name="open_status" noStyle rules={[{ required: true, message: '请选择公开状态' }]} initialValue={'未公开'}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const checked = form.getFieldValue('open_status') === '已公开';
              return (
                <Checkbox
                  checked={checked}
                  onChange={(e) => form.setFieldsValue({ open_status: e.target.checked ? '已公开' : '未公开' })}
                >
                  已公开
                </Checkbox>
              );
            }}
          </Form.Item>
        </Form.Item>
      </ConditionalFormItem>
      <ConditionalFormItem condition="open_status == 已公开 && sub_service !=rejection_review && sub_service !=authorization">
        <Form.Item label="公开日期" name="opened_at">
          <DatePicker style={{ width: '100%' }} placeholder="请选择公开日期" />
        </Form.Item>
        <Form.Item label="公开国家和城市" name="opened_area">
          <Input placeholder="请输入公开国家和城市" />
        </Form.Item>
        <Form.Item label="公开方式" name="opened_type" rules={[{ required: true, message: '请选择公开方式' }]}>
          <Radio.Group>
            <Radio value="线上">线上</Radio>
            <Radio value="线下">线下</Radio>
            <Radio value="其他">其他</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service == appearance_application || sub_service == invention_application || sub_service == utility_application">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="发明人名称" name="inventor_name" rules={[{ required: true, message: '请输入发明人名称' }]}>
              <Input placeholder="证件照上的名字/中文名" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="发明人英文名字" name="inventor_name_en">
              <Input placeholder="请输入英文名" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="发明人地址" name="inventor_address">
              <Input placeholder="证件照上的地址" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="发明人英文地址" name="inventor_address_en">
              <Input placeholder="请输入英文地址" />
            </Form.Item>
          </Col>
        </Row>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service == rejection_review">
        <Form.Item label="驳回理由" name="review_type" rules={[{ required: true, message: '请选择驳回理由' }]}>
          <Radio.Group>
            <Radio value="被驳回">被驳回</Radio>
            <Radio value="被无效">被无效</Radio>
          </Radio.Group>
        </Form.Item>
        <ConditionalFormItem condition="review_type == 被驳回">
          <Form.Item label="驳回原因" name="rejection_reason" rules={[{ required: true, message: '请选择驳回原因' }]}>
            <Radio.Group>
              <Radio value="资料缺陷">资料缺陷</Radio>
              <Radio value="缺乏新颖性">缺乏新颖性</Radio>
            </Radio.Group>
          </Form.Item>
        </ConditionalFormItem>
        <Form.Item label="复审次数" name="postpone_times" rules={[{ required: true, message: '请选择复审次数' }]}>
          <Radio.Group>
            <Radio value="第1次">第1次</Radio>
            <Radio value="第2次">第2次</Radio>
            <Radio value="第3次">第3次</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      <Form.Item label="备注" name="remark">
        <Input.TextArea rows={3} placeholder="请输入备注信息" data-testid="order-remark" />
      </Form.Item>
    </>
  );
};

export default PatentSection;
